import { useFindProject, useUpdateProject } from '@/hooks/useProject';
import Loader from '../ui/Loader';
import { ProjectResponse } from '@/types/projects/ProjectResponse';
import { Control, SubmitHandler, useForm } from 'react-hook-form';
import {
  ProjectFormData,
  ProjectSchema,
  TourOperatorProjectSchema,
} from '@/types/projects/ProjectFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '../ui/Modal';
import StyledInput from '../styled/StyledInput';
import { ParticipantsList } from './CreateProjectModal';
import { useEffect, useMemo, useState } from 'react';
import StyledButton from '../styled/StyledButton';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';

interface ViewProjectModalProps {
  clientId: string;
  projectId: string;
  isTourOperator?: boolean;
  closeModal: () => void;
}

const ViewProjectModal = ({
  clientId,
  projectId,
  isTourOperator: isTourOperatorProp,
  closeModal,
}: ViewProjectModalProps) => {
  const {
    data: projectData,
    isSuccess,
    isLoading,
    isRefetching,
  } = useFindProject(clientId, projectId);
  return isLoading || isRefetching ? (
    <Loader />
  ) : (
    isSuccess && (
      <ViewProjectForm
        closeModal={closeModal}
        project={projectData}
        isTourOperator={isTourOperatorProp}
        key={`project-${projectData.id}`}
      />
    )
  );
};

export default ViewProjectModal;

interface ViewProjectFormProps {
  project: ProjectResponse;
  isTourOperator?: boolean;
  closeModal: () => void;
}

const ViewProjectForm = ({
  project,
  isTourOperator: isTourOperatorProp,
  closeModal,
}: ViewProjectFormProps) => {
  const { t } = useTranslation();
  const { isTourOperator: isTourOperatorFromMe, tProject } =
    useDistributorLabels();
  const isTourOperator = isTourOperatorProp ?? isTourOperatorFromMe;

  const projectFormValues = useMemo<ProjectFormData>(
    () => ({
      name: project.name,
      clientFirstName: project.clientFirstName ?? undefined,
      adults: project.adults.map((adult) => ({
        firstName: adult.firstName ?? undefined,
        lastName: adult.lastName ?? undefined,
      })),
      children: project.children.map((child) => ({
        firstName: child.firstName ?? undefined,
        lastName: child.lastName ?? undefined,
        birthDate: child.birthDate ? new Date(child.birthDate) : undefined,
      })),
    }),
    [project],
  );

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, dirtyFields },
    reset,
  } = useForm<ProjectFormData>({
    mode: 'onChange',
    resolver: zodResolver(
      isTourOperator ? TourOperatorProjectSchema : ProjectSchema,
    ),
    defaultValues: projectFormValues,
  });

  useEffect(() => {
    reset(projectFormValues);
  }, [projectFormValues, reset]);

  const [isEditEnabled, setIsEditEnabled] = useState(false);

  const { mutateAsync: updateProjectData } = useUpdateProject(
    project.clientId,
    project.id,
  );
  const { toastAsync } = useToast();

  const submitForm: SubmitHandler<ProjectFormData> = async (data) => {
    const { status } = await toastAsync(
      updateProjectData({
        name: dirtyFields.name ? data.name : undefined,
        clientFirstName: dirtyFields.clientFirstName
          ? data.clientFirstName
          : undefined,
        adults: dirtyFields.adults
          ? data.adults.map((adult, index) => ({
              id:
                project.adults.length > index
                  ? project.adults[index].id
                  : undefined,
              firstName: dirtyFields.adults?.[index]?.firstName
                ? adult.firstName
                : undefined,
              lastName: dirtyFields.adults?.[index]?.lastName
                ? adult.lastName
                : undefined,
            }))
          : undefined,
        children: dirtyFields.children
          ? data.children.map((child, index) => ({
              id:
                project.children.length > index
                  ? project.children[index].id
                  : undefined,
              firstName: dirtyFields.children?.[index]?.firstName
                ? child.firstName
                : undefined,
              lastName: dirtyFields.children?.[index]?.lastName
                ? child.lastName
                : undefined,
              birthDate: dirtyFields.children?.[index]?.birthDate
                ? child.birthDate
                : undefined,
            }))
          : undefined,
      }),
      {
        success: isTourOperator
          ? tProject('projectUpdatedSuccess')
          : t('project.projectUpdatedSuccess'),
      },
    );

    if (status < 300) closeModal();
  };

  const HeaderNode = isEditEnabled ? (
    <StyledButton
      title={t('common.cancel')}
      onClick={() => {
        setIsEditEnabled(false);
        reset();
      }}
    />
  ) : (
    <StyledButton
      title={t('common.edit')}
      onClick={() => {
        setIsEditEnabled(true);
      }}
    />
  );

  return (
    <Modal
      title={project.name}
      actionTitle={t('common.next')}
      actionOnClick={handleSubmit(submitForm)}
      isActionDisabled={!isValid || !isDirty}
      closeModal={closeModal}
      headerNode={HeaderNode}
      message={t('common.requiredFieldsNote')}
    >
      <div className="flex flex-col gap-6 w-full">
        <StyledInput
          control={control}
          name="name"
          title={isTourOperator ? tProject('name') : t('project.name')}
          placeholder={
            isTourOperator
              ? tProject('namePlaceholder')
              : t('project.namePlaceholder')
          }
          disabled={!isEditEnabled}
        />
        {isTourOperator && (
          <StyledInput
            control={control}
            name="clientFirstName"
            title={tProject('firstName')}
            placeholder={tProject('firstNamePlaceholder')}
            disabled={!isEditEnabled}
          />
        )}
        <div className="max-h-[600px] 2xl:max-h-[700px] overflow-y-auto">
          <ParticipantsList
            control={control as Control<ProjectFormData>}
            isViewMode={!isEditEnabled}
          />
        </div>
      </div>
    </Modal>
  );
};
